import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Check } from 'lucide-react';
import { IMG, SpeakCta, BRAND_GREEN } from './shared';

/* ------------------------------------------------------------------ */
/* Pricing pennant — frosted glass banner hanging from the card's top  */
/* ------------------------------------------------------------------ */

const NAVY = '#0E1A38';
const CORAL = '#F52C52';

// Pennant silhouette with square top corners tapering to a rounded point,
// drawn inside a 150 x 158 box. The stroke variant skips the top edge so the
// shape reads as hanging from the card.
const PENNANT_PATH =
  'M3,0 H147 V110 Q147,116 142,120 L83,149 Q75,153 67,149 L8,120 Q3,116 3,110 V0 Z';
const PENNANT_STROKE_PATH =
  'M3,0 V110 Q3,116 8,120 L67,149 Q75,153 83,149 L142,120 Q147,116 147,110 V0';

const PricingPennant = ({ label, price, unit }) => (
  <div className="mpa-pennant">
    <div className="mpa-pennant-fill" style={{ clipPath: `path('${PENNANT_PATH}')` }} aria-hidden="true" />
    <svg className="mpa-pennant-stroke" viewBox="0 0 150 158" fill="none" aria-hidden="true">
      <path d={PENNANT_STROKE_PATH} stroke="rgba(255,255,255,0.9)" strokeWidth={4} strokeLinecap="round" />
    </svg>
    <div className="mpa-pennant-content">
      <span className="mpa-shimmer mpa-pennant-label" style={{ color: NAVY }}>
        {label}
      </span>
      <span className="mpa-pennant-rule" style={{ backgroundColor: CORAL }} aria-hidden="true" />
      <span className="mpa-pennant-startingat" style={{ color: `${NAVY}99` }}>
        starting at
      </span>
      <span className="mpa-shimmer mpa-pennant-price" style={{ color: CORAL }}>
        {price}
      </span>
      <span className="mpa-pennant-unit" style={{ color: `${NAVY}CC` }}>
        {unit}
      </span>
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/* Off Rent / On Rent coverage toggle                                  */
/* ------------------------------------------------------------------ */

const CONTENT = {
  off: {
    label: 'Off Rent',
    when: 'Marketplace & Private Rentals',
    heading: "When your vehicle isn't being rented.",
    body: 'Billed monthly, Off-Rent insurance keeps your vehicle and business state compliant while insuring it between rentals. Parked, stored, being cleaned, maintained, or otherwise in your possession.',
    points: [
      'Liability, Comprehensive & Collision, Physical damage',
      'Billed monthly, keeps your vehicle & business state compliant',
      'Covers your vehicle, you, and approved staff',
      'Protection during prep, cleaning & downtime',
    ],
    image: {
      src: `${IMG}/rentshield/off-rent-fleet-team.webp`,
      alt: 'A rental business owner and staff standing in front of their vehicle fleet',
      position: 'center 40%',
    },
    price: { price: '$40', unit: '/vehicle/mo' },
  },
  on: {
    label: 'On Rent',
    when: 'During Direct or Private Rental',
    heading: 'When your vehicle is being rented.',
    body: 'On-Rent insurance provides primary, A-rated commercial coverage during an active rental directly through your own rental business.',
    points: [
      'Liability, Comprehensive & Collision, Physical damage',
      'Driver will not need any further coverage',
      'Protects your business, your vehicle, and the driver',
      'Works for leisure, direct, private, rideshare & gig rentals',
    ],
    image: {
      src: `${IMG}/rentshield/on-rent-airport.webp`,
      alt: 'Two travelers getting into their rental car in an airport parking lot',
      position: '75% center',
    },
    price: { price: '$16', unit: '/vehicle/day' },
  },
  direct: {
    label: 'Direct Rental',
    when: 'Direct & Private Rentals',
    heading: 'Insurance for direct and private rentals.',
    body: 'Short and long term rental insurance for direct and private rentals. A-rated primary liability, comprehensive, and collision coverage, billed per day to keep your vehicles and rental business compliant.',
    points: [
      'Primary, A-rated liability, comprehensive & collision',
      'Personalized renter insurance card for every rental',
      'Works for short-term & long-term direct rentals',
      'Billed per day, only pay when the vehicle is rented',
    ],
    image: {
      src: `${IMG}/rentshield/direct-rental-photo.webp`,
      alt: 'Rental operator handing over keys to a renter beside a vehicle',
    },
    price: { price: '$16', unit: '/vehicle/day' },
  },
  gig: {
    label: 'Gig Rental',
    when: 'Rideshare & Gig Economy',
    heading: 'Insurance for rideshare and gig drivers.',
    body: 'Short term and long term gig economy rental insurance for rideshare, delivery, and more. A-rated primary liability, comprehensive, and collision coverage, billed per day to keep your vehicles compliant.',
    points: [
      'Compliant primary coverage for every gig platform',
      'Driver insurance card for Uber, Lyft, DoorDash & more',
      'Billed per day for short & long-term gig rentals',
      'Keeps drivers earning without gaps in coverage',
    ],
    image: {
      src: `${IMG}/rentshield/rideshare-hero-pickup.webp`,
      alt: 'Rideshare driver holding the car door open for a passenger on a city street',
    },
    price: { price: '$16', unit: '/vehicle/day' },
  },
  roadside: {
    label: 'Roadside',
    when: '24/7 Roadside Support',
    heading: 'Help on the road, built in.',
    body: 'Give every renter 24/7 roadside support with smart dispatch and real-time communication, so a breakdown never becomes your problem.',
    points: [
      'Tow, jump-start, lockout, tire change & fuel delivery',
      '24/7 nationwide dispatch with live truck tracking',
      'Follows the driver with no upfront cost at the roadside',
      'Keeps renters moving so breakdowns never reach you',
    ],
    image: {
      src: `${IMG}/inshur/roadside-assistance.webp`,
      alt: 'A roadside assistance technician helping a driver next to an SUV parked on the shoulder at golden hour',
      position: '78% center',
    },
    price: { price: '$10', unit: '/mth' },
  },
  warranty: {
    label: 'Warranty',
    when: 'Mechanical Protection',
    heading: 'Coverage for the repairs that stop revenue.',
    body: 'Mechanical breakdown protection that keeps your rental vehicles on the road instead of sitting in the shop with unexpected repair bills.',
    points: [
      'Engine, transmission & drivetrain coverage',
      'Predictable, fleet-friendly fixed pricing',
      'Fast approved-shop claims keep vehicles earning',
      'Nationwide network of approved repair shops',
    ],
    image: {
      src: `${IMG}/inshur/vehicle-warranty.webp`,
      alt: 'An automotive mechanic inspecting the engine bay of an SUV with a diagnostic tablet in a modern service bay',
    },
    price: { price: '$49', unit: '/mth' },
  },
};

const TABS = ['off', 'on', 'direct', 'gig', 'roadside', 'warranty'];

const CoverageToggle = () => {
  const [tab, setTab] = useState('off');
  const active = CONTENT[tab];

  const listRef = useRef(null);
  const btnRefs = useRef({});
  const [indicator, setIndicator] = useState(null);

  // The green "liquid" indicator rests on the selected pill and slides to the
  // newly selected pill on click.
  const measure = useCallback(() => {
    const btn = btnRefs.current[tab];
    const list = listRef.current;
    if (!btn || !list) return;
    setIndicator({ left: btn.offsetLeft, width: btn.offsetWidth });
  }, [tab]);

  useEffect(() => {
    measure();
  }, [measure]);

  useEffect(() => {
    const list = listRef.current;
    if (!list || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => measure());
    ro.observe(list);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [measure]);

  return (
    <div>
      {/* Toggle — glass pills with a sliding green indicator */}
      <div ref={listRef} role="tablist" aria-label="Coverage options" className="mpa-covtabs">
        {indicator && (
          <span
            aria-hidden="true"
            className="mpa-cov-indicator"
            style={{ width: indicator.width, transform: `translateX(${indicator.left}px)` }}
          />
        )}
        {TABS.map((key) => {
          const isSelected = key === tab;
          return (
            <button
              key={key}
              ref={(el) => {
                btnRefs.current[key] = el;
              }}
              role="tab"
              aria-selected={isSelected}
              onClick={() => setTab(key)}
              className={`mpa-covtab${isSelected ? ' is-selected' : ''}`}
            >
              {CONTENT[key].label}
            </button>
          );
        })}
      </div>

      {/* Panel: text left, squared image aside right */}
      <div key={tab} className="mpa-cov-panel">
        <div className="mpa-cov-text">
          <div className="mpa-cov-kicker">
            <span className="mpa-cov-kicker-label" style={{ color: BRAND_GREEN }}>
              {active.label}
            </span>
            <span className="mpa-cov-kicker-when">&middot; {active.when}</span>
          </div>
          <h4 className="mpa-cov-heading">{active.heading}</h4>
          <p className="mpa-cov-body">{active.body}</p>
          <ul className="mpa-cov-points">
            {active.points.map((p) => (
              <li key={p}>
                <span className="mpa-check">
                  <Check size={12} strokeWidth={3} aria-hidden="true" />
                </span>
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="mpa-cov-media-wrap">
          <div className="mpa-cov-media">
            <img
              src={active.image.src}
              alt={active.image.alt}
              style={{ objectPosition: active.image.position || 'center' }}
            />
            {active.price && (
              <div className="mpa-cov-price">
                <span className="mpa-cov-price-eyebrow">Starting at</span>
                <span className="mpa-cov-price-figure">{active.price.price}</span>
                <span className="mpa-cov-price-unit">{active.price.unit}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* RentShield™ insurance section                                       */
/* ------------------------------------------------------------------ */

const InsuranceSection = ({ logo, bg }) => (
  <section id="private-rental-insurance" className="mpa-ins">
    <div className="mpa-container mpa-ins-inner">
      {/* Lead-in */}
      <div className="mpa-leadin mpa-ins-lead">
        <p className="mpa-eyebrow">But what about insurance?</p>
        <h2 className="mpa-leadin-title">
          Private &amp; direct rental insurance made for your business, vehicles, and drivers.
        </h2>
        <p className="mpa-leadin-sub mpa-leadin-sub--lg">
          Insurance infrastructure built around drivers and vehicles that move between being
          available, actively rented, and returned.
        </p>
      </div>

      {/* Product card */}
      <div
        className="mpa-card mpa-card--dark mpa-ins-card"
        style={{
          backgroundColor: '#050505',
          backgroundImage: `linear-gradient(to bottom, rgba(5,5,5,0.82) 0%, rgba(5,5,5,0.45) 42%, rgba(5,5,5,0.12) 80%, rgba(5,5,5,0) 100%), url(${bg})`,
          backgroundSize: '100% 150%',
          backgroundPosition: 'center bottom',
          backgroundRepeat: 'no-repeat',
        }}
      >
        {/* Pricing pennants — top right corner */}
        <div className="mpa-pennants">
          <PricingPennant label="Off rent" price="$40" unit="/vehicle/mo" />
          <PricingPennant label="On rent" price="$16" unit="/vehicle/day" />
        </div>

        <div className="mpa-card-head">
          <img src={logo} alt="RentShield logo" className="mpa-card-logo" />
        </div>

        <h3 className="mpa-card-tagline">
          Insurance and protection plans for your business, fleets and drivers.
        </h3>
        <p className="mpa-card-desc">
          RentShield&trade; brings together the protections your rental business needs, including
          commercial auto insurance, roadside assistance, and vehicle warranties, all designed to
          protect your vehicles, renters, and the business.
        </p>

        {/* Off Rent / On Rent coverage toggle */}
        <div className="mpa-ins-toggle">
          <CoverageToggle />
        </div>

        <div className="mpa-card-ctas">
          <SpeakCta />
          <a
            href="https://rentshield.io/?getstarted=coverage"
            target="_blank"
            rel="noopener noreferrer"
            className="mpa-api-link"
          >
            Explore the insurance API <span aria-hidden="true">&rarr;</span>
          </a>
        </div>

        <p className="mpa-footnote">
          Insurance products and related services are subject to eligibility, underwriting
          requirements, policy terms, conditions, limitations, and availability. RentShield&trade;
          is powered by INSHUR.
        </p>
      </div>
    </div>
  </section>
);

export default InsuranceSection;
